import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Sparkles,
  Palmtree,
  Building2,
  Mountain,
  Plane,
  Crown,
  Wallet,
  Users,
  User,
  Heart,
  Briefcase,
  Utensils,
  Dumbbell,
  Music,
  Camera,
  Wifi,
  Bed,
  Volume2,
  Eye,
  Calendar,
  Loader2,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

const PREFERENCE_CATEGORIES = {
  travel_style: {
    label: 'Travel Style',
    icon: Palmtree,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
    options: [
      { value: 'beach', label: 'Beach & Resort', icon: Palmtree },
      { value: 'city', label: 'City Explorer', icon: Building2 },
      { value: 'mountain', label: 'Mountain & Nature', icon: Mountain },
      { value: 'adventure', label: 'Adventure & Sports', icon: Plane },
      { value: 'luxury', label: 'Luxury & Premium', icon: Crown },
      { value: 'budget', label: 'Budget Friendly', icon: Wallet },
    ]
  },
  budget_range: {
    label: 'Budget Range (per night)',
    icon: Wallet,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
    options: [
      { value: 'budget', label: '$50 - $100', icon: Wallet },
      { value: 'mid_range', label: '$100 - $200', icon: Wallet },
      { value: 'upper_mid', label: '$200 - $350', icon: Wallet },
      { value: 'luxury', label: '$350 - $500', icon: Crown },
      { value: 'ultra_luxury', label: '$500+', icon: Crown },
    ]
  },
  typical_group: {
    label: 'Who Do You Usually Travel With?',
    icon: Users,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    options: [
      { value: 'solo', label: 'Solo Traveler', icon: User },
      { value: 'couple', label: 'Couple', icon: Heart },
      { value: 'family', label: 'Family', icon: Users },
      { value: 'friends', label: 'Friends Group', icon: Users },
      { value: 'business', label: 'Business Travel', icon: Briefcase },
    ]
  },
  activities: {
    label: 'Activities & Interests',
    icon: Camera,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    options: [
      { value: 'spa', label: 'Spa & Wellness', icon: Sparkles },
      { value: 'fitness', label: 'Fitness & Gym', icon: Dumbbell },
      { value: 'nightlife', label: 'Nightlife & Entertainment', icon: Music },
      { value: 'photography', label: 'Photography', icon: Camera },
      { value: 'local_tours', label: 'Local Tours & Culture', icon: Plane },
    ]
  },
  dietary: {
    label: 'Dietary Preferences',
    icon: Utensils,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    options: [
      { value: 'no_restrictions', label: 'No Restrictions', icon: Utensils },
      { value: 'vegetarian', label: 'Vegetarian', icon: Utensils },
      { value: 'vegan', label: 'Vegan', icon: Utensils },
      { value: 'halal', label: 'Halal', icon: Utensils },
      { value: 'kosher', label: 'Kosher', icon: Utensils },
    ]
  },
  room_preferences: {
    label: 'Room Preferences',
    icon: Bed,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    options: [
      { value: 'view', label: 'Great View', icon: Eye },
      { value: 'quiet', label: 'Quiet Location', icon: Volume2 },
      { value: 'near_amenities', label: 'Near Amenities', icon: Building2 },
      { value: 'high_floor', label: 'High Floor', icon: Building2 },
    ]
  },
  work_needs: {
    label: 'Work & Business Needs',
    icon: Briefcase,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    options: [
      { value: 'fast_wifi', label: 'Fast WiFi', icon: Wifi },
      { value: 'workspace', label: 'Desk & Workspace', icon: Briefcase },
      { value: 'meeting_space', label: 'Meeting Rooms', icon: Users },
      { value: 'twenty_four_seven', label: '24/7 Access', icon: Calendar },
    ]
  },
  special_occasions: {
    label: 'Special Occasions',
    icon: Sparkles,
    color: 'text-pink-600',
    bgColor: 'bg-pink-100',
    options: [
      { value: 'honeymoon', label: 'Honeymoon', icon: Heart },
      { value: 'anniversary', label: 'Anniversary', icon: Heart },
      { value: 'birthday', label: 'Birthday Celebration', icon: Sparkles },
      { value: 'none', label: 'Regular Stay', icon: Calendar },
    ]
  }
};

export const PreferenceCollectionModal = ({ open, onOpenChange, guestId, onComplete }) => {
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(false);

  const handleToggle = (category, value) => {
    setPreferences(prev => {
      const current = prev[category] || [];
      const isSelected = current.includes(value);
      
      return {
        ...prev,
        [category]: isSelected 
          ? current.filter(v => v !== value)
          : [...current, value]
      };
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
            const response = await fetch(`${BACKEND_URL}/api/guests/me/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          preferences,
          consent: {
            manual_preferences: true,
            consented_at: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      toast.success('✨ Preferences saved! Bitsy will use these to find your perfect stay.');
      onComplete?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onOpenChange(false);
    onComplete?.();
  };

  const selectedCount = Object.values(preferences).flat().length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl max-h-[85vh] overflow-y-auto"
        data-testid="preference-collection-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-2xl heading-font">
                Personalize Your Experience
              </DialogTitle>
              <DialogDescription className="text-base mt-1">
                To serve you better next time, select your preferences. This helps Bitsy find the right hotel each time — <span className="font-semibold text-foreground">so you don't waste your valuable time</span>.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-6 space-y-8">
          {Object.entries(PREFERENCE_CATEGORIES).map(([categoryKey, category]) => {
            const CategoryIcon = category.icon;
            const selectedInCategory = preferences[categoryKey] || [];

            return (
              <div key={categoryKey} className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg ${category.bgColor} flex items-center justify-center`}>
                    <CategoryIcon className={`w-4 h-4 ${category.color}`} />
                  </div>
                  <h3 className="font-semibold heading-font text-base">
                    {category.label}
                  </h3>
                  {selectedInCategory.length > 0 && (
                    <Badge variant="secondary" className="ml-auto">
                      {selectedInCategory.length} selected
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {category.options.map((option) => {
                    const OptionIcon = option.icon;
                    const isSelected = selectedInCategory.includes(option.value);

                    return (
                      <div
                        key={option.value}
                        onClick={() => handleToggle(categoryKey, option.value)}
                        className={`
                          relative flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer
                          transition-all duration-200 hover:shadow-md hover:scale-[1.02]
                          ${isSelected 
                            ? 'border-violet-500 bg-violet-50 shadow-sm' 
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                          }
                        `}
                        data-testid={`preference-option-${categoryKey}-${option.value}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggle(categoryKey, option.value)}
                          className="pointer-events-none"
                        />
                        <div className="flex items-center gap-2 flex-1">
                          <OptionIcon className={`w-4 h-4 ${isSelected ? 'text-violet-600' : 'text-gray-500'}`} />
                          <Label className={`text-sm cursor-pointer ${isSelected ? 'font-semibold text-violet-900' : 'text-gray-700'}`}>
                            {option.label}
                          </Label>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Separator className="mt-4" />
              </div>
            );
          })}

          {/* Privacy Notice */}
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-green-900 mb-1">Your Privacy Matters</p>
                <p className="text-green-800">
                  We only save what you select. You can view, edit, or delete these preferences anytime from your Profile Settings. 
                  These preferences help Bitsy AI recommend hotels that match your style.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t">
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">
              {selectedCount === 0 && "No preferences selected yet"}
              {selectedCount > 0 && (
                <>
                  <span className="font-semibold text-foreground">{selectedCount} preference{selectedCount !== 1 ? 's' : ''}</span> selected
                  <span className="ml-2">— Bitsy will use these to find your perfect stay</span>
                </>
              )}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={handleSkip}
              disabled={loading}
              data-testid="preference-skip-btn"
            >
              Skip for Now
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading || selectedCount === 0}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
              data-testid="preference-save-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Save Preferences
                  {selectedCount > 0 && (
                    <ChevronRight className="w-4 h-4 ml-1" />
                  )}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
